// ============================================================================
// budgetService — Operational Financial Layer
// ----------------------------------------------------------------------------
// Stored-denormalisation model: every node carries `actualSpend` and the
// `logExpense` / `reverseExpense` transactions walk the caller-supplied
// ancestor chain to mutate each parent atomically. Reversals are negative
// entries (with `reversesExpenseId`) — never deletes — to keep the audit
// ledger append-only.
//
// Concurrency: every mutation runs inside a single Firestore transaction
// that reads the workspace + every ancestor doc + the prior expense (for
// reversal) before writing, so concurrent expense logs cannot drift the
// aggregate. Amounts are integer minor units to avoid float rounding.
//
// Module gate: every public mutation short-circuits when
// `isModuleEnabled(workspace, 'budget')` is false. This is the architectural
// equivalent of the "DeltaProcessor middleware gate" called out in spec §4.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Firestore,
  type Transaction,
} from 'firebase/firestore'

import { firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import {
  BUDGET_CATEGORIES,
  BudgetCategoryLimitExceededError,
  WORKSPACE_EXPENSES_COLLECTION,
  WORKSPACE_BUDGET_SUMMARY_COLLECTION,
  computeBudgetBand,
  normalizeBudgetCategory,
  type BudgetAuditAction,
  type BudgetCategory,
  type BudgetEntityRef,
  type BudgetEntityType,
  type BurnRateBucket,
  type TopSpenderEntry,
  type WorkspaceBudgetSummary,
  type WorkspaceExpense,
  type WorkspaceExpenseReceipt,
} from '../types/budget'
import type { Workspace } from '../types/workspace'
import { isModuleEnabled } from '../utils/workspaceModules'
import { formatMoney } from '../utils/formatMoney'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
export { buildBudgetEntityChain, type BudgetEntityChainItem, type BudgetChainNode } from './budgetEntityChain'

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class BudgetModuleDisabledError extends Error {
  constructor() {
    super('Budget module is disabled for this workspace')
    this.name = 'BudgetModuleDisabledError'
  }
}

export class BudgetCurrencyMismatchError extends Error {
  expected: string
  received: string
  constructor(expected: string, received: string) {
    super(`Currency mismatch: workspace uses ${expected}, expense was ${received}`)
    this.name = 'BudgetCurrencyMismatchError'
    this.expected = expected
    this.received = received
  }
}

export class BudgetEntityMissingError extends Error {
  ref: BudgetEntityRef
  constructor(ref: BudgetEntityRef) {
    super(`Budget entity no longer exists: ${ref.type} ${ref.id}`)
    this.name = 'BudgetEntityMissingError'
    this.ref = ref
  }
}

export class BudgetReversalTargetError extends Error {
  expenseId: string
  reason: 'missing' | 'already-reversed' | 'is-reversal'
  constructor(expenseId: string, reason: 'missing' | 'already-reversed' | 'is-reversal') {
    super(`Cannot reverse expense ${expenseId}: ${reason}`)
    this.name = 'BudgetReversalTargetError'
    this.expenseId = expenseId
    this.reason = reason
  }
}

export class PendingExpenseReviewError extends Error {
  pendingExpenseId: string
  constructor(pendingExpenseId: string, reason: string) {
    super(`Cannot review pending expense ${pendingExpenseId}: ${reason}`)
    this.name = 'PendingExpenseReviewError'
    this.pendingExpenseId = pendingExpenseId
  }
}

// ---------------------------------------------------------------------------
// Refs / utilities
// ---------------------------------------------------------------------------

const workspaceRef = (db: Firestore, workspaceId: string) =>
  doc(db, 'workspaces', workspaceId)

const expensesCollection = (db: Firestore) =>
  collection(db, WORKSPACE_EXPENSES_COLLECTION)

const expenseDoc = (db: Firestore, expenseId: string) =>
  doc(db, WORKSPACE_EXPENSES_COLLECTION, expenseId)

const pendingExpenseDoc = (db: Firestore, workspaceId: string, pendingExpenseId: string) =>
  doc(db, 'workspaces', workspaceId, 'pending_expenses', pendingExpenseId)

const summaryDoc = (db: Firestore, workspaceId: string) =>
  doc(db, WORKSPACE_BUDGET_SUMMARY_COLLECTION, workspaceId)

const BURN_RATE_WINDOW_DAYS = 90
const TOP_SPENDERS_CAP = 20

const toIsoDate = (timestampMs: number): string => {
  const date = new Date(timestampMs)
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

const todayIso = () => toIsoDate(Date.now())

/** Read the existing summary doc (or build an empty one). Called inside the
 * outer transaction so the recompute is atomic with the expense write. */
const readSummaryOrEmpty = (
  snapData: DocumentData | undefined,
  workspaceId: string,
  currency: string,
): WorkspaceBudgetSummary => ({
  workspaceId,
  currency,
  totalBudgetLimit: Number(snapData?.totalBudgetLimit ?? 0),
  totalActualSpend: Number(snapData?.totalActualSpend ?? 0),
  totalEstimatedCost: Number(snapData?.totalEstimatedCost ?? 0),
  dailyBuckets: Array.isArray(snapData?.dailyBuckets) ? (snapData!.dailyBuckets as BurnRateBucket[]) : [],
  topSpenders: Array.isArray(snapData?.topSpenders) ? (snapData!.topSpenders as TopSpenderEntry[]) : [],
  overBudgetCount: Number(snapData?.overBudgetCount ?? 0),
  breachCount: Number(snapData?.breachCount ?? 0),
  overrideCount: Number(snapData?.overrideCount ?? 0),
  lastUpdatedAt: (snapData?.lastUpdatedAt as Timestamp) ?? null,
})

/** Roll the 90-day window forward and apply the day's net delta. Returns a
 * new array (oldest-first). */
const applyBucketDelta = (
  current: BurnRateBucket[],
  isoDate: string,
  spendDelta: number,
  entryDelta: number,
): BurnRateBucket[] => {
  const map = new Map(current.map((bucket) => [bucket.date, bucket]))
  const existing = map.get(isoDate) ?? { date: isoDate, spend: 0, entries: 0 }
  map.set(isoDate, {
    date: isoDate,
    spend: existing.spend + spendDelta,
    entries: existing.entries + entryDelta,
  })
  // Drop entries older than the 90-day window relative to the newest date.
  const sorted = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
  if (sorted.length <= BURN_RATE_WINDOW_DAYS) return sorted
  return sorted.slice(sorted.length - BURN_RATE_WINDOW_DAYS)
}

/** Merge a single entity's spend/limit/title into the top-spenders ranking. */
const upsertTopSpender = (
  current: TopSpenderEntry[],
  entry: TopSpenderEntry,
): TopSpenderEntry[] => {
  const filtered = current.filter((existing) => existing.entityId !== entry.entityId)
  if (entry.spend > 0) filtered.push(entry)
  filtered.sort((a, b) => b.spend - a.spend)
  return filtered.slice(0, TOP_SPENDERS_CAP)
}

interface SummaryDelta {
  isoDate?: string
  spendDelta?: number
  entryDelta?: number
  breachDelta?: number
  overrideDelta?: number
  totalBudgetLimitDelta?: number
  totalActualSpendDelta?: number
  /** When a Goal-level node was just updated: surface it for the top-spenders
   * list. Caller passes the post-write spend so we don't need to re-read. */
  goalUpdate?: TopSpenderEntry
  /** Same for Task-level. */
  taskUpdate?: TopSpenderEntry
  /** Whether the touched leaf entity is now over budget. The summary tracks a
   * monotonic over-budget count by node, so we only flip when state transitions. */
  overBudgetTransition?: 'enter' | 'exit' | null
}

const writeSummary = (
  transaction: Transaction,
  db: Firestore,
  workspaceId: string,
  currency: string,
  prevSnapData: DocumentData | undefined,
  delta: SummaryDelta,
) => {
  const summary = readSummaryOrEmpty(prevSnapData, workspaceId, currency)
  summary.currency = currency

  if (delta.isoDate !== undefined && (delta.spendDelta || delta.entryDelta)) {
    summary.dailyBuckets = applyBucketDelta(
      summary.dailyBuckets,
      delta.isoDate,
      delta.spendDelta ?? 0,
      delta.entryDelta ?? 0,
    )
  }
  if (delta.totalActualSpendDelta) {
    summary.totalActualSpend = Math.max(0, summary.totalActualSpend + delta.totalActualSpendDelta)
  }
  if (delta.totalBudgetLimitDelta) {
    summary.totalBudgetLimit = Math.max(0, summary.totalBudgetLimit + delta.totalBudgetLimitDelta)
  }
  if (delta.breachDelta) summary.breachCount = Math.max(0, summary.breachCount + delta.breachDelta)
  if (delta.overrideDelta) summary.overrideCount = Math.max(0, summary.overrideCount + delta.overrideDelta)
  if (delta.overBudgetTransition === 'enter') summary.overBudgetCount += 1
  if (delta.overBudgetTransition === 'exit') summary.overBudgetCount = Math.max(0, summary.overBudgetCount - 1)

  if (delta.goalUpdate) summary.topSpenders = upsertTopSpender(summary.topSpenders, delta.goalUpdate)
  if (delta.taskUpdate) summary.topSpenders = upsertTopSpender(summary.topSpenders, delta.taskUpdate)

  transaction.set(summaryDoc(db, workspaceId), {
    ...summary,
    lastUpdatedAt: serverTimestamp(),
  })
}

const asDocRef = (db: Firestore, docPath: string): DocumentReference => {
  // Firestore's doc() needs segments split when given a path containing slashes.
  const segments = docPath.split('/').filter(Boolean)
  if (segments.length < 2 || segments.length % 2 !== 0) {
    throw new Error(`Invalid Firestore doc path: ${docPath}`)
  }
  const [first, ...rest] = segments
  return doc(db, first, ...rest)
}

const sanitizeAmount = (value: number): number => {
  if (!Number.isFinite(value)) throw new Error('Expense amount must be a finite number')
  if (!Number.isInteger(value)) throw new Error('Expense amount must be integer minor units')
  return value
}

const readActualSpend = (data: DocumentData | undefined): number => {
  const raw = Number(data?.actualSpend)
  return Number.isFinite(raw) ? raw : 0
}

const readBudgetLimit = (data: DocumentData | undefined): number | null => {
  const raw = data?.budgetLimit
  if (raw === null || raw === undefined) return null
  const numeric = Number(raw)
  return Number.isFinite(numeric) ? numeric : null
}

const readWorkspaceCurrency = (workspace: DocumentData | undefined): string => {
  const code = workspace?.settings?.currency
  if (typeof code !== 'string' || code.trim().length === 0) {
    throw new Error('Workspace currency is not configured (workspace.settings.currency)')
  }
  return code.trim().toUpperCase()
}
const getPartsInTz = (date: Date, timeZone: string | undefined) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
    const parts = formatter.formatToParts(date)
    let year = date.getFullYear()
    let month = date.getMonth() + 1
    let day = date.getDate()
    for (const p of parts) {
      if (p.type === 'year') year = Number(p.value)
      if (p.type === 'month') month = Number(p.value)
      if (p.type === 'day') day = Number(p.value)
    }
    return { year, month, day }
  } catch {
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
  }
}


const requireBudgetEnabled = (workspace: DocumentData | undefined) => {
  if (!isModuleEnabled(workspace as Pick<Workspace, 'modules'>, 'budget')) throw new BudgetModuleDisabledError()
}

const inferElementType = (data: DocumentData | undefined): BudgetEntityType => {
  const type = data?.type
  return type === 'GOAL' || type === 'TASK' ? type : 'TASK'
}

// ---------------------------------------------------------------------------
// Audit emission
// ---------------------------------------------------------------------------

type AuditContext = {
  actorId: string
  actorName?: string | null
}

export type BudgetHealthSummary = {
  totalBudgetLimit: number
  totalActualSpend: number
  totalEstimatedCost: number
  utilization: number | null
  band: ReturnType<typeof computeBudgetBand>
  watchCount: number
  overBudgetCount: number
}

const asPositiveNumber = (value: unknown): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0
}

export const summarizeBudgetHealth = (
  rows: Array<{ budgetLimit?: unknown; actualSpend?: unknown; estimatedCost?: unknown }>,
): BudgetHealthSummary => {
  const totals = rows.reduce(
    (acc, row) => {
      const budgetLimit = asPositiveNumber(row.budgetLimit)
      const actualSpend = asPositiveNumber(row.actualSpend)
      const estimatedCost = asPositiveNumber(row.estimatedCost)
      const ratio = budgetLimit > 0 ? actualSpend / budgetLimit : 0
      return {
        totalBudgetLimit: acc.totalBudgetLimit + budgetLimit,
        totalActualSpend: acc.totalActualSpend + actualSpend,
        totalEstimatedCost: acc.totalEstimatedCost + estimatedCost,
        watchCount: acc.watchCount + (budgetLimit > 0 && ratio >= 0.8 && ratio < 1 ? 1 : 0),
        overBudgetCount: acc.overBudgetCount + (budgetLimit > 0 && ratio >= 1 ? 1 : 0),
      }
    },
    { totalBudgetLimit: 0, totalActualSpend: 0, totalEstimatedCost: 0, watchCount: 0, overBudgetCount: 0 },
  )
  const utilization = totals.totalBudgetLimit > 0 ? totals.totalActualSpend / totals.totalBudgetLimit : null
  return {
    ...totals,
    utilization,
    band: computeBudgetBand(totals.totalActualSpend, totals.totalBudgetLimit),
  }
}

const summarizeAction = (action: BudgetAuditAction, target: string, amount?: number, currency?: string) => {
  switch (action) {
    case 'expense.log':
      return `Logged expense ${formatAmount(amount, currency)} on ${target}`
    case 'expense.reverse':
      return `Reversed expense ${formatAmount(amount, currency)} on ${target}`
    case 'expense.exceeded':
      return `${target} exceeded its budget`
    case 'budget.create':
      return `Set budget for ${target}`
    case 'budget.update':
      return `Updated budget for ${target}`
    case 'budget.override':
      return `Force-completed over-budget ${target}`
    case 'vendor.create':
      return `Created vendor ${target}`
    case 'vendor.update':
      return `Updated vendor ${target}`
    case 'vendor.delete':
      return `Deleted vendor ${target}`
    case 'recurring.create':
      return `Created recurring rule ${target}`
    case 'recurring.update':
      return `Updated recurring rule ${target}`
    case 'recurring.deactivate':
      return `Deactivated recurring rule ${target}`
  }
}

const formatAmount = (amount: number | undefined, currency: string | undefined): string => {
  if (typeof amount !== 'number' || !currency) return ''
  return formatMoney(amount, currency)
}

// ---------------------------------------------------------------------------
// Public: subscribe to a node's direct-expense feed
// ---------------------------------------------------------------------------

/** Live feed of expenses logged DIRECTLY against the given entity. The parent
 * roll-up (`actualSpend`) is on the node doc itself; this stream powers the
 * per-node ledger preview in the Financials tab. */
export const subscribeEntityExpenses = (
  workspaceId: string,
  entityId: string,
  callback: (entries: WorkspaceExpense[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId || !entityId) {
    callback([])
    return () => {}
  }
  const q = query(
    expensesCollection(db),
    where('workspaceId', '==', workspaceId),
    where('linkedEntityId', '==', entityId),
    orderBy('timestamp', 'desc'),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      const entries: WorkspaceExpense[] = snapshot.docs.map((snap) => {
        const data = snap.data() as Partial<WorkspaceExpense>
        return {
          id: snap.id,
          workspaceId: String(data.workspaceId ?? workspaceId),
          amount: Number(data.amount ?? 0),
          currency: String(data.currency ?? ''),
          description: String(data.description ?? ''),
          category: normalizeBudgetCategory(data.category),
          linkedEntityId: String(data.linkedEntityId ?? entityId),
          linkedEntityType: (data.linkedEntityType as BudgetEntityType) ?? 'CHECKLIST',
          linkedEntityPath: Array.isArray(data.linkedEntityPath) ? data.linkedEntityPath : [],
          reversesExpenseId: data.reversesExpenseId ?? null,
          receipt: (data.receipt as WorkspaceExpenseReceipt | null | undefined) ?? null,
          createdById: String(data.createdById ?? ''),
          createdByName: data.createdByName ?? null,
          timestamp: (data.timestamp as Timestamp) ?? Timestamp.fromMillis(0),
          vendorId: data.vendorId != null ? String(data.vendorId) : null,
          tags: Array.isArray(data.tags) ? data.tags.map(String) : [],
          taxAmount: data.taxAmount != null ? Number(data.taxAmount) : null,
          taxInclusive: data.taxInclusive != null ? Boolean(data.taxInclusive) : true,
          paymentMethod: (data.paymentMethod as WorkspaceExpense['paymentMethod']) ?? null,
          attachments: Array.isArray(data.attachments) ? data.attachments.map(String) : [],
          recurringId: data.recurringId != null ? String(data.recurringId) : null,
          isIncome: Boolean(data.isIncome ?? false),
        }
      })
      callback(entries)
    },
    (error) => {
      console.warn('[budgetService] expenses subscription error', { workspaceId, entityId }, error)
      onError?.(error)
    },
  )
}

export const subscribeWorkspaceExpenses = (
  workspaceId: string,
  callback: (entries: WorkspaceExpense[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
): (() => void) => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  const q = query(
    expensesCollection(db),
    where('workspaceId', '==', workspaceId),
    orderBy('timestamp', 'desc'),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      const entries: WorkspaceExpense[] = snapshot.docs.map((snap) => {
        const data = snap.data() as Partial<WorkspaceExpense>
        return {
          id: snap.id,
          workspaceId: String(data.workspaceId ?? workspaceId),
          amount: Number(data.amount ?? 0),
          currency: String(data.currency ?? ''),
          description: String(data.description ?? ''),
          category: normalizeBudgetCategory(data.category),
          linkedEntityId: String(data.linkedEntityId ?? ''),
          linkedEntityType: (data.linkedEntityType as BudgetEntityType) ?? 'CHECKLIST',
          linkedEntityPath: Array.isArray(data.linkedEntityPath) ? data.linkedEntityPath : [],
          reversesExpenseId: data.reversesExpenseId ?? null,
          receipt: (data.receipt as WorkspaceExpenseReceipt | null | undefined) ?? null,
          createdById: String(data.createdById ?? ''),
          createdByName: data.createdByName ?? null,
          timestamp: (data.timestamp as Timestamp) ?? Timestamp.fromMillis(0),
          vendorId: data.vendorId != null ? String(data.vendorId) : null,
          tags: Array.isArray(data.tags) ? data.tags.map(String) : [],
          taxAmount: data.taxAmount != null ? Number(data.taxAmount) : null,
          taxInclusive: data.taxInclusive != null ? Boolean(data.taxInclusive) : true,
          paymentMethod: (data.paymentMethod as WorkspaceExpense['paymentMethod']) ?? null,
          attachments: Array.isArray(data.attachments) ? data.attachments.map(String) : [],
          recurringId: data.recurringId != null ? String(data.recurringId) : null,
          isIncome: Boolean(data.isIncome ?? false),
        }
      })
      callback(entries)
    },
    (error) => {
      console.warn('[budgetService] workspace expenses subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

// ---------------------------------------------------------------------------
// Public: logExpense (positive or zero amount, append-only)
// ---------------------------------------------------------------------------

export interface LogExpenseInput {
  workspaceId: string
  entityChain: BudgetEntityRef[] // leaf-first, e.g. [CHECKLIST, TASK, GOAL]
  amount: number                 // integer minor units, >= 0
  currency: string               // must match workspace.settings.currency
  description: string
  category?: BudgetCategory
  receipt?: WorkspaceExpenseReceipt | null
  audit: AuditContext
  /** Titles for the breadcrumb snapshot, leaf-first. Optional; if omitted the
   * service reads `title`/`name` off each ancestor doc instead. */
  entityTitles?: string[]
  vendorId?: string | null
  tags?: string[]
  taxAmount?: number | null
  taxInclusive?: boolean
  paymentMethod?: 'cash' | 'card' | 'bank' | 'upi' | 'other' | null
  attachments?: string[]
  recurringId?: string | null
  isIncome?: boolean
}

export interface LogExpenseResult {
  expenseId: string
  exceeded: boolean
  overCategoryLimit?: 'monthly' | 'quarterly' | 'yearly' | null
  queuedForApproval?: boolean
}

export const logExpense = async (
  input: LogExpenseInput,
  db: Firestore = firestoreDb,
): Promise<LogExpenseResult> => {
  const amount = sanitizeAmount(input.amount)
  if (amount < 0) throw new Error('logExpense amount must be >= 0; use reverseExpense for negatives')
  if (input.entityChain.length === 0) throw new Error('logExpense requires at least the leaf entity in entityChain')

  const wsRef = workspaceRef(db, input.workspaceId)
  const wsInitialSnap = await getDoc(wsRef)
  if (!wsInitialSnap.exists()) throw new Error('Workspace no longer exists')
  const workspaceInitial = wsInitialSnap.data()
  requireBudgetEnabled(workspaceInitial)
  const expectedCurrency = readWorkspaceCurrency(workspaceInitial)
  if (input.currency.trim().toUpperCase() !== expectedCurrency) {
    throw new BudgetCurrencyMismatchError(expectedCurrency, input.currency)
  }

  const leaf = input.entityChain[0]
  const normalizedCategory = normalizeBudgetCategory(input.category)

  // Approval threshold check: queue to pending_expenses if amount > threshold
  const threshold = workspaceInitial?.settings?.expenseApprovalThresholdMinor
  if (threshold !== undefined && threshold !== null && amount > threshold) {
    const pendingRef = doc(collection(db, 'workspaces', input.workspaceId, 'pending_expenses'))
    await setDoc(pendingRef, {
      workspaceId: input.workspaceId,
      checklistItemId: leaf.type === 'CHECKLIST' ? leaf.id : (input.entityChain.find((e) => e.type === 'CHECKLIST')?.id ?? leaf.id),
      taskId: input.entityChain.find((e) => e.type === 'TASK')?.id ?? null,
      entityChain: input.entityChain,
      entityTitles: input.entityTitles ?? null,
      amountMinor: amount,
      currencyCode: expectedCurrency,
      category: normalizedCategory,
      description: input.description.trim(),
      receiptUrl: input.receipt?.externalUrl ?? '',
      receiptStoragePath: input.receipt?.storagePath ?? '',
      note: input.description.trim(),
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? 'Member',
      status: 'PENDING',
      createdAt: serverTimestamp(),
      vendorId: input.vendorId ?? null,
      tags: input.tags ?? [],
      taxAmount: input.taxAmount ?? null,
      taxInclusive: input.taxInclusive !== false,
      paymentMethod: input.paymentMethod ?? null,
      attachments: input.attachments ?? [],
      recurringId: input.recurringId ?? null,
      isIncome: Boolean(input.isIncome),
    })
    return { expenseId: pendingRef.id, exceeded: false, queuedForApproval: true }
  }

  // Pre-calculate running spend for category limits
  const tz = typeof workspaceInitial?.timezone === 'string' ? workspaceInitial.timezone : undefined
  const nowParts = getPartsInTz(new Date(), tz)
  const currentQuarter = Math.floor((nowParts.month - 1) / 3)
  let monthSpend = 0
  let quarterSpend = 0
  let yearSpend = 0

  try {
    const expensesSnap = await getDocs(
      query(collection(db, WORKSPACE_EXPENSES_COLLECTION), where('workspaceId', '==', input.workspaceId)),
    )
    expensesSnap.docs.forEach((docSnap) => {
      const d = docSnap.data()
      if (d.category !== normalizedCategory) return
      if (d.reversesExpenseId) return
      const a = Number(d.amount ?? 0)
      if (a <= 0) return
      const ts = d.timestamp ?? d.createdAt
      const expDate = ts?.toDate ? ts.toDate() : (typeof ts === 'string' ? new Date(ts) : new Date())
      const parts = getPartsInTz(expDate, tz)
      if (parts.year === nowParts.year) {
        yearSpend += a
        if (Math.floor((parts.month - 1) / 3) === currentQuarter) {
          quarterSpend += a
        }
        if (parts.month === nowParts.month) {
          monthSpend += a
        }
      }
    })
  } catch (err) {
    console.warn('[budgetService] failed to compute category running spend', err)
  }

  const categoryLimitRef = doc(db, 'budget_category_limits', `${input.workspaceId}__${normalizedCategory}`)

  const expenseRef = doc(expensesCollection(db))

  const ancestorRefs = input.entityChain.map((ref) => asDocRef(db, ref.docPath))

  let exceeded = false
  let exceededTargetTitle = leaf.id
  let overCategoryLimit: 'monthly' | 'quarterly' | 'yearly' | null = null

  await runTransaction(db, async (transaction) => {
    // 1) Read workspace (module gate + currency), summary doc, category limits, and every
    //    ancestor doc — all reads must precede any writes (Firestore rule).
    const summaryRef = summaryDoc(db, input.workspaceId)
    const [wsSnap, summarySnap, categoryLimitSnap, ...ancestorSnaps] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(summaryRef),
      transaction.get(categoryLimitRef),
      ...ancestorRefs.map((ref) => transaction.get(ref)),
    ])
    if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
    const workspace = wsSnap.data()
    requireBudgetEnabled(workspace)
    const expected = readWorkspaceCurrency(workspace)
    if (input.currency.trim().toUpperCase() !== expected) {
      throw new BudgetCurrencyMismatchError(expected, input.currency)
    }

    // Category limit enforcement check
    if (categoryLimitSnap.exists()) {
      const limitData = categoryLimitSnap.data()
      const monthlyLimit = limitData.monthlyLimit !== undefined && limitData.monthlyLimit !== null ? Number(limitData.monthlyLimit) : null
      const quarterlyLimit = limitData.quarterlyLimit !== undefined && limitData.quarterlyLimit !== null ? Number(limitData.quarterlyLimit) : null
      const yearlyLimit = limitData.yearlyLimit !== undefined && limitData.yearlyLimit !== null ? Number(limitData.yearlyLimit) : null

      const wouldSpendMonth = monthSpend + amount
      const wouldSpendQuarter = quarterSpend + amount
      const wouldSpendYear = yearSpend + amount

      const enforcementMode = workspace.modules?.budgetEnforcement === 'block' ? 'block' : 'warn'

      if (monthlyLimit !== null && wouldSpendMonth > monthlyLimit) {
        if (enforcementMode === 'block') {
          throw new BudgetCategoryLimitExceededError(normalizedCategory, 'monthly', monthlyLimit, wouldSpendMonth)
        }
        overCategoryLimit = 'monthly'
      } else if (quarterlyLimit !== null && wouldSpendQuarter > quarterlyLimit) {
        if (enforcementMode === 'block') {
          throw new BudgetCategoryLimitExceededError(normalizedCategory, 'quarterly', quarterlyLimit, wouldSpendQuarter)
        }
        overCategoryLimit = 'quarterly'
      } else if (yearlyLimit !== null && wouldSpendYear > yearlyLimit) {
        if (enforcementMode === 'block') {
          throw new BudgetCategoryLimitExceededError(normalizedCategory, 'yearly', yearlyLimit, wouldSpendYear)
        }
        overCategoryLimit = 'yearly'
      }
    }

    // 2) Snapshot titles for the linkedEntityPath stamp.
    const path = ancestorSnaps.map((snap, index) => {
      if (!snap.exists()) throw new BudgetEntityMissingError(input.entityChain[index])
      const data = snap.data()
      const title = input.entityTitles?.[index]
        ?? (typeof data?.title === 'string' ? data.title : typeof data?.name === 'string' ? data.name : input.entityChain[index].id)
      return { type: input.entityChain[index].type, id: input.entityChain[index].id, title }
    })

    // 3) Write the expense entry.
    transaction.set(expenseRef, {
      workspaceId: input.workspaceId,
      amount,
      currency: expected,
      description: input.description.trim(),
      category: normalizedCategory,
      linkedEntityId: leaf.id,
      linkedEntityType: leaf.type,
      linkedEntityPath: path,
      reversesExpenseId: null,
      receipt: input.receipt ?? null,
      vendorId: input.vendorId ?? null,
      tags: input.tags ?? [],
      taxAmount: input.taxAmount ?? null,
      taxInclusive: input.taxInclusive !== false,
      paymentMethod: input.paymentMethod ?? null,
      attachments: input.attachments ?? [],
      recurringId: input.recurringId ?? null,
      isIncome: Boolean(input.isIncome),
      createdById: input.audit.actorId,
      createdByName: input.audit.actorName ?? null,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
    })

    // 4) Bump actualSpend on every ancestor and detect threshold crossing
    //    at the leaf (the entity the expense was logged against).
    let leafBecameOverBudget = false
    const summaryDelta: SummaryDelta = {
      isoDate: todayIso(),
      spendDelta: amount,
      entryDelta: 1,
    }
    ancestorSnaps.forEach((snap, index) => {
      const before = readActualSpend(snap.data())
      const after = before + amount
      const ref = ancestorRefs[index]
      const data = snap.data()
      const title = path[index].title
      const limit = readBudgetLimit(data)
      transaction.update(ref, { actualSpend: after, lastUpdatedAt: serverTimestamp() })
      const entityKind = input.entityChain[index].type
      // Goal-level totals drive the workspace summary (avoids double-counting).
      if (entityKind === 'GOAL') {
        summaryDelta.totalActualSpendDelta = (summaryDelta.totalActualSpendDelta ?? 0) + amount
        summaryDelta.goalUpdate = {
          entityId: input.entityChain[index].id,
          entityType: 'GOAL',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      } else if (entityKind === 'TASK') {
        summaryDelta.taskUpdate = {
          entityId: input.entityChain[index].id,
          entityType: 'TASK',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      }
      if (index === 0) {
        if (limit !== null && before <= limit && after > limit) {
          exceeded = true
          exceededTargetTitle = typeof data?.title === 'string' ? data.title : leaf.id
          leafBecameOverBudget = true
        }
      }
    })
    if (leafBecameOverBudget) {
      summaryDelta.breachDelta = 1
      summaryDelta.overBudgetTransition = 'enter'
    }

    // 5) Audit ledger — expense.log + optional expense.exceeded, same txn.
    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'expense.log',
      activityType: 'BUDGET_EXPENSE',
      targetId: leaf.id,
      nodeType: leaf.type,
      targetTitle: path[0].title,
      summary: summarizeAction('expense.log', path[0].title, amount, expected) ?? 'Logged expense',
      payload: {
        expenseId: expenseRef.id,
        amount,
        currency: expected,
        category: normalizeBudgetCategory(input.category),
        linkedEntityPath: path,
        receipt: input.receipt ?? null,
        recurringId: input.recurringId ?? null,
      },
    })

    if (exceeded) {
      logAuditEvent(db, transaction, workspace, {
        workspaceId: input.workspaceId,
        actorId: input.audit.actorId,
        actorName: input.audit.actorName ?? null,
        action: 'expense.exceeded',
        activityType: 'BUDGET_BREACH',
        targetId: leaf.id,
        nodeType: leaf.type,
        targetTitle: exceededTargetTitle,
        summary: summarizeAction('expense.exceeded', exceededTargetTitle) ?? 'Budget exceeded',
        payload: {
          expenseId: expenseRef.id,
          amount,
          currency: expected,
        },
      })
    }

    // 6) Workspace-wide summary doc — rolled forward in the same transaction.
    writeSummary(
      transaction,
      db,
      input.workspaceId,
      expected,
      summarySnap.exists() ? summarySnap.data() : undefined,
      summaryDelta,
    )
  })

  return { expenseId: expenseRef.id, exceeded, overCategoryLimit }
}

// ---------------------------------------------------------------------------
// Public: approve/reject helper pending expenses
// ---------------------------------------------------------------------------

export interface ApprovePendingExpenseInput {
  workspaceId: string
  pendingExpenseId: string
  audit: AuditContext
  reviewNote?: string | null
}

export const approvePendingExpense = async (
  input: ApprovePendingExpenseInput,
  db: Firestore = firestoreDb,
): Promise<{ expenseId: string }> => {
  const wsRef = workspaceRef(db, input.workspaceId)
  const pendingRef = pendingExpenseDoc(db, input.workspaceId, input.pendingExpenseId)
  const expenseRef = doc(expensesCollection(db))
  let createdExpenseId = expenseRef.id

  await runTransaction(db, async (transaction) => {
    const summaryRef = summaryDoc(db, input.workspaceId)
    const [wsSnap, pendingSnap, summarySnap] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(pendingRef),
      transaction.get(summaryRef),
    ])
    if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
    if (!pendingSnap.exists()) throw new PendingExpenseReviewError(input.pendingExpenseId, 'missing')
    const workspace = wsSnap.data()
    requireBudgetEnabled(workspace)
    const pending = pendingSnap.data()
    if (pending.workspaceId !== input.workspaceId) throw new PendingExpenseReviewError(input.pendingExpenseId, 'workspace-mismatch')
    if (pending.status !== 'PENDING') throw new PendingExpenseReviewError(input.pendingExpenseId, 'already-reviewed')

    const checklistId = String(pending.checklistItemId ?? '')
    if (!checklistId) throw new PendingExpenseReviewError(input.pendingExpenseId, 'missing-checklist')
    const checklistRef = asDocRef(db, `checklists/${checklistId}`)
    const checklistSnap = await transaction.get(checklistRef)
    if (!checklistSnap.exists()) throw new BudgetEntityMissingError({ type: 'CHECKLIST', id: checklistId, docPath: `checklists/${checklistId}` })
    const checklistData = checklistSnap.data()

    const chain: BudgetEntityRef[] = [{
      type: 'CHECKLIST',
      id: checklistId,
      docPath: `checklists/${checklistId}`,
    }]
    const parentRefs: DocumentReference[] = []
    let parentId = typeof checklistData.parentContainerId === 'string' ? checklistData.parentContainerId : null
    for (let depth = 0; parentId && depth < 4; depth += 1) {
      const parentRef = asDocRef(db, `elements/${parentId}`)
      const parentSnap = await transaction.get(parentRef)
      if (!parentSnap.exists()) break
      const parentData = parentSnap.data()
      const parentType = inferElementType(parentData)
      chain.push({ type: parentType, id: parentSnap.id, docPath: `elements/${parentSnap.id}` })
      parentRefs.push(parentRef)
      parentId = typeof parentData.parentId === 'string' ? parentData.parentId : null
      if (parentType === 'GOAL') break
    }

    const ancestorRefs = [checklistRef, ...parentRefs]
    const ancestorSnaps: DocumentSnapshot<DocumentData>[] = [checklistSnap]
    for (const parentRef of parentRefs) {
      ancestorSnaps.push(await transaction.get(parentRef))
    }

    const amount = sanitizeAmount(Number(pending.amountMinor ?? 0))
    if (amount < 0) throw new PendingExpenseReviewError(input.pendingExpenseId, 'negative-amount')
    const expected = readWorkspaceCurrency(workspace)
    const currency = String(pending.currencyCode ?? '').trim().toUpperCase()
    if (currency !== expected) throw new BudgetCurrencyMismatchError(expected, currency)
    const category = normalizeBudgetCategory(pending.category)

    const path = ancestorSnaps.map((snap, index) => {
      if (!snap.exists()) throw new BudgetEntityMissingError(chain[index])
      const data = snap.data()
      const title = typeof data?.title === 'string' ? data.title : typeof data?.name === 'string' ? data.name : chain[index].id
      return { type: chain[index].type, id: chain[index].id, title }
    })
    const leaf = chain[0]
    const targetTitle = path[0]?.title ?? leaf.id

    transaction.set(expenseRef, {
      workspaceId: input.workspaceId,
      amount,
      currency: expected,
      description: String(pending.note ?? '').trim() || `Helper receipt from ${String(pending.actorName ?? 'Helper')}`,
      category,
      linkedEntityId: leaf.id,
      linkedEntityType: leaf.type,
      linkedEntityPath: path,
      reversesExpenseId: null,
      receipt: {
        storagePath: typeof pending.receiptStoragePath === 'string' ? pending.receiptStoragePath : null,
        externalUrl: typeof pending.receiptUrl === 'string' ? pending.receiptUrl : null,
      },
      createdById: input.audit.actorId,
      createdByName: input.audit.actorName ?? null,
      timestamp: serverTimestamp(),
      sourcePendingExpenseId: input.pendingExpenseId,
      submittedById: pending.actorId ?? null,
      submittedByName: pending.actorName ?? null,
    })

    let leafBecameOverBudget = false
    const summaryDelta: SummaryDelta = {
      isoDate: todayIso(),
      spendDelta: amount,
      entryDelta: 1,
    }
    ancestorSnaps.forEach((snap, index) => {
      const before = readActualSpend(snap.data())
      const after = before + amount
      const data = snap.data()
      const limit = readBudgetLimit(data)
      const title = path[index].title
      transaction.update(ancestorRefs[index], { actualSpend: after, lastUpdatedAt: serverTimestamp() })
      const entityKind = chain[index].type
      if (entityKind === 'GOAL') {
        summaryDelta.totalActualSpendDelta = (summaryDelta.totalActualSpendDelta ?? 0) + amount
        summaryDelta.goalUpdate = {
          entityId: chain[index].id,
          entityType: 'GOAL',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      } else if (entityKind === 'TASK') {
        summaryDelta.taskUpdate = {
          entityId: chain[index].id,
          entityType: 'TASK',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      }
      if (index === 0 && limit !== null && before <= limit && after > limit) {
        leafBecameOverBudget = true
      }
    })
    if (leafBecameOverBudget) {
      summaryDelta.breachDelta = 1
      summaryDelta.overBudgetTransition = 'enter'
    }

    transaction.update(pendingRef, {
      status: 'APPROVED',
      reviewedBy: input.audit.actorId,
      reviewedById: input.audit.actorId,
      reviewedByName: input.audit.actorName ?? null,
      reviewedAt: serverTimestamp(),
      reviewNote: input.reviewNote ?? null,
      expenseId: expenseRef.id,
    })

    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'expense.approve',
      activityType: 'BUDGET_EXPENSE',
      targetId: leaf.id,
      nodeType: leaf.type,
      targetTitle,
      summary: `Approved expense for ${targetTitle}`,
      payload: {
        expenseId: expenseRef.id,
        pendingExpenseId: input.pendingExpenseId,
        amount,
        currency: expected,
      },
    })

    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'expense.log',
      activityType: 'BUDGET_EXPENSE',
      targetId: leaf.id,
      nodeType: leaf.type,
      targetTitle,
      summary: summarizeAction('expense.log', targetTitle, amount, expected) ?? 'Logged expense',
      payload: {
        expenseId: expenseRef.id,
        amount,
        currency: expected,
        category,
        linkedEntityPath: path,
        sourcePendingExpenseId: input.pendingExpenseId,
      },
    })
    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'EXPENSE_APPROVED_FROM_INTAKE',
      activityType: 'BUDGET_EXPENSE',
      targetId: leaf.id,
      nodeType: leaf.type,
      targetTitle,
      summary: `Approved helper receipt for ${targetTitle}.`,
      payload: {
        expenseId: expenseRef.id,
        pendingExpenseId: input.pendingExpenseId,
        helperId: pending.actorId ?? null,
        amount,
        currency: expected,
      },
    })

    writeSummary(
      transaction,
      db,
      input.workspaceId,
      expected,
      summarySnap.exists() ? summarySnap.data() : undefined,
      summaryDelta,
    )
    createdExpenseId = expenseRef.id
  })

  return { expenseId: createdExpenseId }
}

export const rejectPendingExpense = async (
  input: ApprovePendingExpenseInput & { note?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  await runTransaction(db, async (transaction) => {
    const wsRef = workspaceRef(db, input.workspaceId)
    const pendingRef = pendingExpenseDoc(db, input.workspaceId, input.pendingExpenseId)
    const [wsSnap, pendingSnap] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(pendingRef),
    ])
    if (!pendingSnap.exists()) throw new PendingExpenseReviewError(input.pendingExpenseId, 'missing')
    const pending = pendingSnap.data()
    if (pending.workspaceId !== input.workspaceId) throw new PendingExpenseReviewError(input.pendingExpenseId, 'workspace-mismatch')
    if (pending.status !== 'PENDING') throw new PendingExpenseReviewError(input.pendingExpenseId, 'already-reviewed')
    const note = input.note?.trim() || input.reviewNote?.trim() || pending.note || null
    transaction.update(pendingRef, {
      status: 'REJECTED',
      reviewedBy: input.audit.actorId,
      reviewedById: input.audit.actorId,
      reviewedByName: input.audit.actorName ?? null,
      reviewedAt: serverTimestamp(),
      reviewNote: note,
      note,
    })

    logAuditEvent(db, transaction, wsSnap.exists() ? wsSnap.data() : undefined, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'expense.reject',
      activityType: 'BUDGET_EXPENSE',
      targetId: input.pendingExpenseId,
      nodeType: 'PENDING_EXPENSE',
      targetTitle: 'Pending expense',
      summary: 'Rejected pending expense',
      payload: {
        pendingExpenseId: input.pendingExpenseId,
        note,
      },
    })
  })
}

// ---------------------------------------------------------------------------
// Public: reverseExpense (negative entry, immutable history)
// ---------------------------------------------------------------------------

export interface ReverseExpenseInput {
  workspaceId: string
  originalExpenseId: string
  entityChain: BudgetEntityRef[]
  description: string
  audit: AuditContext
}

export const reverseExpense = async (
  input: ReverseExpenseInput,
  db: Firestore = firestoreDb,
): Promise<{ reversalId: string }> => {
  const wsRef = workspaceRef(db, input.workspaceId)
  const originalRef = expenseDoc(db, input.originalExpenseId)
  const reversalRef = doc(expensesCollection(db))
  const ancestorRefs = input.entityChain.map((ref) => asDocRef(db, ref.docPath))
  const leaf = input.entityChain[0]

  await runTransaction(db, async (transaction) => {
    const summaryRef = summaryDoc(db, input.workspaceId)
    const [wsSnap, originalSnap, summarySnap, ...ancestorSnaps] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(originalRef),
      transaction.get(summaryRef),
      ...ancestorRefs.map((ref) => transaction.get(ref)),
    ])
    if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
    const workspace = wsSnap.data()
    requireBudgetEnabled(workspace)
    if (!originalSnap.exists()) {
      throw new BudgetReversalTargetError(input.originalExpenseId, 'missing')
    }
    const original = originalSnap.data()
    if (original.reversesExpenseId) {
      throw new BudgetReversalTargetError(input.originalExpenseId, 'is-reversal')
    }
    if (original.workspaceId !== input.workspaceId) {
      throw new Error('Workspace mismatch on reversal')
    }
    const reversalAmount = -Number(original.amount ?? 0)
    if (!Number.isInteger(reversalAmount)) {
      throw new Error('Original expense amount is not a valid integer')
    }
    const currency = readWorkspaceCurrency(workspace)
    const category = normalizeBudgetCategory(original.category)

    // Snapshot the breadcrumb. Reuse the original's path so the reversal is
    // anchored even if titles have since drifted.
    const path = Array.isArray(original.linkedEntityPath) ? original.linkedEntityPath : []

    transaction.set(reversalRef, {
      workspaceId: input.workspaceId,
      amount: reversalAmount,
      currency,
      description: input.description.trim(),
      category,
      linkedEntityId: leaf.id,
      linkedEntityType: leaf.type,
      linkedEntityPath: path,
      reversesExpenseId: input.originalExpenseId,
      receipt: null,
      createdById: input.audit.actorId,
      createdByName: input.audit.actorName ?? null,
      timestamp: serverTimestamp(),
    })

    const summaryDelta: SummaryDelta = {
      isoDate: todayIso(),
      spendDelta: reversalAmount, // negative
      entryDelta: 1,
    }
    let leafExitedOverBudget = false
    ancestorSnaps.forEach((snap, index) => {
      if (!snap.exists()) throw new BudgetEntityMissingError(input.entityChain[index])
      const data = snap.data()
      const before = readActualSpend(data)
      const after = Math.max(0, before + reversalAmount)
      transaction.update(ancestorRefs[index], { actualSpend: after, lastUpdatedAt: serverTimestamp() })
      const limit = readBudgetLimit(data)
      const title = path[index]?.title ?? input.entityChain[index].id
      const kind = input.entityChain[index].type
      if (kind === 'GOAL') {
        summaryDelta.totalActualSpendDelta = (summaryDelta.totalActualSpendDelta ?? 0) + reversalAmount
        summaryDelta.goalUpdate = {
          entityId: input.entityChain[index].id,
          entityType: 'GOAL',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      } else if (kind === 'TASK') {
        summaryDelta.taskUpdate = {
          entityId: input.entityChain[index].id,
          entityType: 'TASK',
          title,
          spend: after,
          budgetLimit: limit,
          percentConsumed: limit && limit > 0 ? after / limit : null,
        }
      }
      if (index === 0 && limit !== null && before > limit && after <= limit) {
        leafExitedOverBudget = true
      }
    })
    if (leafExitedOverBudget) summaryDelta.overBudgetTransition = 'exit'

    const targetTitle = path[0]?.title ?? leaf.id
    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'expense.reverse',
      activityType: 'BUDGET_EXPENSE',
      targetId: leaf.id,
      nodeType: leaf.type,
      targetTitle,
      summary: summarizeAction('expense.reverse', targetTitle, reversalAmount, currency) ?? 'Reversed expense',
      payload: {
        reversalId: reversalRef.id,
        originalExpenseId: input.originalExpenseId,
        amount: reversalAmount,
        currency,
        category,
      },
    })

    writeSummary(
      transaction,
      db,
      input.workspaceId,
      currency,
      summarySnap.exists() ? summarySnap.data() : undefined,
      summaryDelta,
    )
  })

  return { reversalId: reversalRef.id }
}

// ---------------------------------------------------------------------------
// Public: bulkReverseExpenses (admin reconciler)
// ---------------------------------------------------------------------------

export interface BulkReverseExpensesInput {
  workspaceId: string
  /** Pairs of expense + its ancestor chain. Caller is responsible for
   * supplying the correct chain per row (the same one used when the original
   * was logged). */
  reversals: Array<{ originalExpenseId: string; entityChain: BudgetEntityRef[] }>
  reason: string
  audit: AuditContext
}

/** Issues N reversal entries sequentially. Each reversal is its own atomic
 * transaction; we don't pack them into one because Firestore transactions
 * have a 500-write cap and we don't want a single bad row to roll back the
 * entire batch. Failures surface as `failures` in the returned summary. */
export const bulkReverseExpenses = async (
  input: BulkReverseExpensesInput,
  db: Firestore = firestoreDb,
): Promise<{ reversed: string[]; failures: Array<{ expenseId: string; reason: string }> }> => {
  const reversed: string[] = []
  const failures: Array<{ expenseId: string; reason: string }> = []
  for (const row of input.reversals) {
    try {
      const { reversalId } = await reverseExpense({
        workspaceId: input.workspaceId,
        originalExpenseId: row.originalExpenseId,
        entityChain: row.entityChain,
        description: input.reason,
        audit: input.audit,
      }, db)
      reversed.push(reversalId)
    } catch (error) {
      failures.push({
        expenseId: row.originalExpenseId,
        reason: error instanceof Error ? error.message : String(error),
      })
    }
  }
  return { reversed, failures }
}

// ---------------------------------------------------------------------------
// Public: set / update budgetLimit on a node (transactional, audited)
// ---------------------------------------------------------------------------

export interface SetBudgetLimitInput {
  workspaceId: string
  entity: BudgetEntityRef
  /** New limit in integer minor units, or null to clear. */
  budgetLimit: number | null
  audit: AuditContext
  /** Optional. Used for the audit `targetTitle` if the doc has no title. */
  entityTitle?: string
}

// ---------------------------------------------------------------------------
// Public: set / update budgetEnforcement on a workspace
// ---------------------------------------------------------------------------

export const setBudgetEnforcement = async (
  workspaceId: string,
  mode: 'warn' | 'block',
  db: Firestore = firestoreDb,
): Promise<void> => {
  const wsRef = workspaceRef(db, workspaceId)
  await updateDoc(wsRef, {
    'modules.budgetEnforcement': mode,
  })
}

export const setBudgetLimit = async (
  input: SetBudgetLimitInput,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const entityRef = asDocRef(db, input.entity.docPath)
  const wsRef = workspaceRef(db, input.workspaceId)

  if (input.budgetLimit !== null) sanitizeAmount(input.budgetLimit)

  await runTransaction(db, async (transaction) => {
    const summaryRef = summaryDoc(db, input.workspaceId)
    const [wsSnap, entitySnap, summarySnap] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(entityRef),
      transaction.get(summaryRef),
    ])
    if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
    const workspace = wsSnap.data()
    requireBudgetEnabled(workspace)
    if (!entitySnap.exists()) throw new BudgetEntityMissingError(input.entity)

    const before = readBudgetLimit(entitySnap.data())
    const isCreate = before === null && input.budgetLimit !== null
    const action: BudgetAuditAction = isCreate ? 'budget.create' : 'budget.update'

    transaction.update(entityRef, {
      budgetLimit: input.budgetLimit,
      lastUpdatedAt: serverTimestamp(),
    })

    const data = entitySnap.data()
    const targetTitle = input.entityTitle
      ?? (typeof data?.title === 'string' ? data.title : input.entity.id)

    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action,
      activityType: 'BUDGET_LIMIT',
      targetId: input.entity.id,
      nodeType: input.entity.type,
      targetTitle,
      summary: summarizeAction(action, targetTitle) ?? 'Budget updated',
      payload: { before, after: input.budgetLimit },
    })

    // Workspace-wide totals: only Goal-level limits affect the rollup total
    // (avoids the parent/child double-count).
    const currency = readWorkspaceCurrency(workspace)
    const limitDelta = (input.budgetLimit ?? 0) - (before ?? 0)
    const summaryDelta: SummaryDelta = {}
    if (input.entity.type === 'GOAL' && limitDelta !== 0) {
      summaryDelta.totalBudgetLimitDelta = limitDelta
    }
    // The top-spender row's `budgetLimit` and `percentConsumed` may change.
    const currentSpend = readActualSpend(data)
    if (input.entity.type === 'GOAL') {
      summaryDelta.goalUpdate = {
        entityId: input.entity.id,
        entityType: 'GOAL',
        title: targetTitle,
        spend: currentSpend,
        budgetLimit: input.budgetLimit,
        percentConsumed: input.budgetLimit && input.budgetLimit > 0 ? currentSpend / input.budgetLimit : null,
      }
    } else if (input.entity.type === 'TASK') {
      summaryDelta.taskUpdate = {
        entityId: input.entity.id,
        entityType: 'TASK',
        title: targetTitle,
        spend: currentSpend,
        budgetLimit: input.budgetLimit,
        percentConsumed: input.budgetLimit && input.budgetLimit > 0 ? currentSpend / input.budgetLimit : null,
      }
    }
    writeSummary(
      transaction,
      db,
      input.workspaceId,
      currency,
      summarySnap.exists() ? summarySnap.data() : undefined,
      summaryDelta,
    )
  })
}

// ---------------------------------------------------------------------------
// Public: OWNER override (force-complete over-budget Task/Goal)
// ---------------------------------------------------------------------------

export interface OverrideOverBudgetInput {
  workspaceId: string
  entity: BudgetEntityRef
  reason: string
  audit: AuditContext
}

/** Records the override decision in the audit ledger. The actual completion
 * write (workflowStatus → COMPLETE) remains the caller's responsibility so we
 * don't entangle budget logic with the broader completion machinery. */
export const recordBudgetOverride = async (
  input: OverrideOverBudgetInput,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const wsRef = workspaceRef(db, input.workspaceId)
  const entityRef = asDocRef(db, input.entity.docPath)

  await runTransaction(db, async (transaction) => {
    const summaryRef = summaryDoc(db, input.workspaceId)
    const [wsSnap, entitySnap, summarySnap] = await Promise.all([
      transaction.get(wsRef),
      transaction.get(entityRef),
      transaction.get(summaryRef),
    ])
    if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
    const workspace = wsSnap.data()
    requireBudgetEnabled(workspace)
    if (!isWorkspaceOwner(workspace, input.audit.actorId)) {
      throw new Error('Only the workspace owner can override budget enforcement')
    }
    if (!entitySnap.exists()) throw new BudgetEntityMissingError(input.entity)

    const data = entitySnap.data()
    const targetTitle = typeof data?.title === 'string' ? data.title : input.entity.id

    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.audit.actorId,
      actorName: input.audit.actorName ?? null,
      action: 'budget.override',
      activityType: 'BUDGET_OVERRIDE',
      targetId: input.entity.id,
      nodeType: input.entity.type,
      targetTitle,
      summary: summarizeAction('budget.override', targetTitle) ?? 'Budget override',
      payload: {
        reason: input.reason.trim(),
        actualSpend: readActualSpend(data),
        budgetLimit: readBudgetLimit(data),
      },
    })

    writeSummary(
      transaction,
      db,
      input.workspaceId,
      readWorkspaceCurrency(workspace),
      summarySnap.exists() ? summarySnap.data() : undefined,
      { overrideDelta: 1 },
    )
  })
}

// ---------------------------------------------------------------------------
// Workspace-wide currency change (OWNER, blocked once expenses exist)
// ---------------------------------------------------------------------------

/**
 * @internal Only callable from the changeWorkspaceCurrency Owner Console callable.
 * Do NOT import from client UI.
 */
export const changeWorkspaceCurrency = async (
  workspaceId: string,
  newCurrency: string,
  audit: AuditContext,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const wsRef = workspaceRef(db, workspaceId)
  const normalised = newCurrency.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(normalised)) throw new Error('Currency must be a 3-letter ISO 4217 code')

  // Check OWNER + no expenses outside the transaction (count query). The
  // transaction below re-asserts the owner check on the latest snapshot.
  const wsSnap = await getDoc(wsRef)
  if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
  if (!isWorkspaceOwner(wsSnap.data(), audit.actorId)) {
    throw new Error('Only the workspace owner can change the currency')
  }
  const q = query(expensesCollection(db), where('workspaceId', '==', workspaceId))
  // We only need to know "has any" — a one-result read keeps this cheap.
  const probe = await import('firebase/firestore').then((m) => m.getDocs(m.query(q, m.limit(1))))
  if (!probe.empty) {
    throw new Error('Cannot change workspace currency once expenses have been recorded')
  }

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(wsRef)
    if (!snap.exists()) throw new Error('Workspace no longer exists')
    const workspace = snap.data()
    if (!isWorkspaceOwner(workspace, audit.actorId)) {
      throw new Error('Only the workspace owner can change the currency')
    }
    transaction.update(wsRef, {
      'settings.currency': normalised,
      lastUpdatedAt: serverTimestamp(),
    })
    logAuditEvent(db, transaction, workspace, {
      workspaceId,
      actorId: audit.actorId,
      actorName: audit.actorName ?? null,
      action: 'budget.update',
      activityType: 'BUDGET_CURRENCY',
      targetId: workspaceId,
      nodeType: 'WORKSPACE',
      targetTitle: workspace.name ?? workspaceId,
      summary: `Workspace currency set to ${normalised}`,
      payload: { before: workspace.settings?.currency ?? null, after: normalised },
    })
  })
}

// ---------------------------------------------------------------------------
// Selectors used by UI summary / Workspace export shape (typed alias).
// ---------------------------------------------------------------------------

export type WorkspaceBudgetGate = Pick<Workspace, 'modules' | 'settings'>

// ---------------------------------------------------------------------------
// Public: subscribe to the pre-aggregated workspace summary (Hub dashboard)
// ---------------------------------------------------------------------------

export const subscribeWorkspaceBudgetSummary = (
  workspaceId: string,
  callback: (summary: WorkspaceBudgetSummary | null) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    callback(null)
    return () => {}
  }
  return onSnapshot(
    summaryDoc(db, workspaceId),
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      const data = snap.data()
      callback({
        workspaceId,
        currency:
          typeof data.currency === 'string' && data.currency.trim()
            ? data.currency.trim().toUpperCase()
            : (() => {
                console.warn(
                  `[formatMoney] Workspace ${workspaceId} summary has no currency set — display fell back to USD.`,
                )
                return 'USD'
              })(),
        totalBudgetLimit: Number(data.totalBudgetLimit ?? 0),
        totalActualSpend: Number(data.totalActualSpend ?? 0),
        totalEstimatedCost: Number(data.totalEstimatedCost ?? 0),
        dailyBuckets: Array.isArray(data.dailyBuckets) ? (data.dailyBuckets as BurnRateBucket[]) : [],
        topSpenders: Array.isArray(data.topSpenders) ? (data.topSpenders as TopSpenderEntry[]) : [],
        overBudgetCount: Number(data.overBudgetCount ?? 0),
        breachCount: Number(data.breachCount ?? 0),
        overrideCount: Number(data.overrideCount ?? 0),
        lastUpdatedAt: (data.lastUpdatedAt as Timestamp) ?? null,
      })
    },
    (error) => {
      console.warn('[budgetService] summary subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

// ---------------------------------------------------------------------------
// Public: canChangeWorkspaceCurrency check
// ---------------------------------------------------------------------------

export const canChangeWorkspaceCurrency = async (
  workspaceId: string,
  db: Firestore = firestoreDb,
): Promise<{ allowed: boolean; reason?: string }> => {
  if (!workspaceId) return { allowed: false, reason: 'Workspace ID is required' }
  try {
    const probe = await getDocs(
      query(collection(db, WORKSPACE_EXPENSES_COLLECTION), where('workspaceId', '==', workspaceId)),
    )
    if (!probe.empty) {
      return { allowed: false, reason: 'Currency locked — an expense has been logged.' }
    }
    return { allowed: true }
  } catch (err) {
    console.warn('[budgetService] canChangeWorkspaceCurrency error', err)
    return { allowed: true }
  }
}

// ---------------------------------------------------------------------------
// Public: bulkLogExpenses (CSV import engine)
// ---------------------------------------------------------------------------

export interface BulkLogExpenseRow {
  date: string
  amount: number
  currency: string
  description: string
  category: string
  linkedEntityId: string
  vendor?: string | null
  paymentMethod?: 'cash' | 'card' | 'bank' | 'upi' | 'other' | null
  taxAmount?: number | null
  isIncome?: boolean
}

export interface BulkLogExpensesInput {
  workspaceId: string
  rows: BulkLogExpenseRow[]
  actor: AuditContext
  onProgress?: (committed: number, total: number) => void
}

export const bulkLogExpenses = async (
  input: BulkLogExpensesInput,
  db: Firestore = firestoreDb,
): Promise<{ committed: number; rejected: number }> => {
  const wsRef = workspaceRef(db, input.workspaceId)
  const wsSnap = await getDoc(wsRef)
  if (!wsSnap.exists()) throw new Error('Workspace no longer exists')
  const workspace = wsSnap.data()
  requireBudgetEnabled(workspace)
  const expectedCurrency = readWorkspaceCurrency(workspace)

  // Fetch vendors in workspace for vendor name resolution
  const vendorsSnap = await getDocs(
    query(collection(db, 'budget_vendors'), where('workspaceId', '==', input.workspaceId)),
  )
  const vendorMap = new Map<string, string>()
  vendorsSnap.docs.forEach((d) => {
    const vData = d.data()
    if (typeof vData.name === 'string') {
      vendorMap.set(vData.name.trim().toLowerCase(), d.id)
    }
  })

  // Resolve unique entity IDs
  const uniqueEntityIds = Array.from(new Set(input.rows.map((r) => r.linkedEntityId).filter(Boolean)))
  const resolvedEntities = new Map<string, { type: BudgetEntityType; id: string; title: string }>()

  await Promise.all(
    uniqueEntityIds.map(async (id) => {
      try {
        const elemSnap = await getDoc(doc(db, 'elements', id))
        if (elemSnap.exists()) {
          const eData = elemSnap.data()
          resolvedEntities.set(id, {
            type: (eData?.type as BudgetEntityType) || 'TASK',
            id,
            title: typeof eData?.title === 'string' ? eData.title : typeof eData?.name === 'string' ? eData.name : id,
          })
          return
        }
      } catch {
        // Fall through
      }

      try {
        const clSnap = await getDoc(doc(db, 'checklists', id))
        if (clSnap.exists()) {
          const clData = clSnap.data()
          resolvedEntities.set(id, {
            type: 'CHECKLIST',
            id,
            title: typeof clData?.title === 'string' ? clData.title : typeof clData?.name === 'string' ? clData.name : id,
          })
          return
        }
      } catch {
        // Not found
      }
    }),
  )

  const validRows: Array<{
    row: BulkLogExpenseRow
    resolvedEntity: { type: BudgetEntityType; id: string; title: string }
    vendorId: string | null
  }> = []
  let rejected = 0

  for (const row of input.rows) {
    if (!row.currency || row.currency.trim().toUpperCase() !== expectedCurrency) {
      rejected++
      continue
    }
    const cat = row.category as BudgetCategory
    if (!cat || !BUDGET_CATEGORIES.includes(cat)) {
      rejected++
      continue
    }
    const resolvedEntity = resolvedEntities.get(row.linkedEntityId)
    if (!resolvedEntity) {
      rejected++
      continue
    }

    let vendorId: string | null = null
    if (row.vendor && typeof row.vendor === 'string') {
      const match = vendorMap.get(row.vendor.trim().toLowerCase())
      if (match) vendorId = match
    }

    validRows.push({ row, resolvedEntity, vendorId })
  }

  let committed = 0
  const BATCH_SIZE = 250

  for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
    const chunk = validRows.slice(i, i + BATCH_SIZE)
    const batch = writeBatch(db)

    for (const item of chunk) {
      const { row, resolvedEntity, vendorId } = item
      const expenseRef = doc(expensesCollection(db))
      const amount = Math.round(Number(row.amount))

      batch.set(expenseRef, {
        workspaceId: input.workspaceId,
        amount,
        currency: expectedCurrency,
        description: row.description?.trim() || 'Imported expense',
        category: row.category,
        linkedEntityId: resolvedEntity.id,
        linkedEntityType: resolvedEntity.type,
        linkedEntityPath: [{ type: resolvedEntity.type, id: resolvedEntity.id, title: resolvedEntity.title }],
        vendorId,
        paymentMethod: row.paymentMethod ?? null,
        taxAmount: row.taxAmount ?? null,
        taxInclusive: true,
        attachments: [],
        tags: [],
        isIncome: Boolean(row.isIncome),
        createdById: input.actor.actorId,
        createdByName: input.actor.actorName ?? null,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
      })
    }

    await batch.commit()
    committed += chunk.length
    input.onProgress?.(committed, validRows.length)
  }

  // Single audit event for bulk import
  await runTransaction(db, async (txn) => {
    const freshWs = await txn.get(wsRef)
    logAuditEvent(db, txn, freshWs.data(), {
      workspaceId: input.workspaceId,
      actorId: input.actor.actorId,
      actorName: input.actor.actorName ?? null,
      action: 'budget.bulk.import',
      activityType: 'CREATE',
      targetId: input.workspaceId,
      nodeType: 'BUDGET',
      targetTitle: 'Bulk import',
      summary: `Bulk imported ${committed} expenses (${rejected} rejected)`,
      payload: {
        totalRows: input.rows.length,
        committedCount: committed,
        rejectedCount: rejected,
      },
    })
  })

  return { committed, rejected }
}

/** Phase 3 integration boundary for server-applied form budget effects. */
export const recordSpendFromForm = async (_params: {
  formSubmissionId: string
  lineId: string
  amountMinor: number
}): Promise<void> => {
  void _params
  throw new Error('budgetService.recordSpendFromForm: not implemented (Phase 3)')
}

/**
 * Update the expense approval threshold for a workspace.
 * A null value disables the threshold (leave empty for helper-only).
 */
export const updateExpenseApprovalThreshold = async (
  workspaceId: string,
  minorOrNull: number | null,
): Promise<void> => {
  const wsRef = doc(firestoreDb, 'workspaces', workspaceId)
  await updateDoc(wsRef, {
    'settings.expenseApprovalThresholdMinor': minorOrNull,
  })
}

export const resolveWorkspaceCurrency = (workspace?: { id?: string; settings?: { currency?: string } } | null): string => {
  const candidate = workspace?.settings?.currency
  if (candidate && typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim().toUpperCase()
  }
  if (workspace?.id) {
    console.warn(`[formatMoney] Workspace ${workspace.id} has no currency set — display fell back to USD`)
  }
  return 'USD'
}

